// Langues : anglais (par défaut), français (langue source du code), espagnol. Le code reste écrit en français ; un
// dictionnaire (clé = phrase française exacte → [anglais, espagnol]) traduit tout ce qui s'affiche, grâce à un
// traducteur de page (textes, infobulles, libellés d'accessibilité — y compris ce qui apparaît plus tard), et ce qui ne
// s'affiche pas (voix, textes dessinés) via t(). Les clés contenant ${…} sont des modèles : « « ${nom} » validée ✓ »
// reconnaît « « Sourire » validée ✓ » ; la traduction y place {1}, {2}… (eux-mêmes traduits s'ils sont connus).
import dictApp from './dict/app';
import dictCreate from './dict/create';
import dictVoice from './dict/voice';
import dictTalk from './dict/talk';
import dictStage from './dict/stage';
import dictWorld from './dict/world';

export type Lang = 'en' | 'fr' | 'es';
export type Entry = [en: string, es: string];

const KEY = 'simulacre.lang';
export const LANGS: ReadonlyArray<{ id: Lang; label: string; name: string }> = [
  { id: 'en', label: 'EN', name: 'English' }, { id: 'fr', label: 'FR', name: 'Français' }, { id: 'es', label: 'ES', name: 'Español' },
];

export const lang: Lang = (() => {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'en' || v === 'fr' || v === 'es') return v;
  } catch { /* stockage indisponible */ }
  return 'en';
})();

/** Code de langue pour la synthèse et la reconnaissance vocales. */
export const speechLang = ({ en: 'en-US', fr: 'fr-FR', es: 'es-ES' } as const)[lang];

/** Change de langue (enregistrée) et recharge la page : tout se reconstruit dans la nouvelle langue. */
export function setLang(l: Lang) {
  try { localStorage.setItem(KEY, l); } catch { /* ignoré */ }
  location.reload();
}

const norm = (s: string) => s.replace(/[’]/g, '\'').replace(/\s+/g, ' ').trim();
const exact = new Map<string, string>();
const patterns: Array<{ re: RegExp; out: string; n: number; fixed: number }> = [];
const html = new Map<string, string>();
/** Traductions connues (sorties) : un texte déjà traduit n'est pas retraduit (« Large » ≠ clé française « Large »). */
const outputs = new Set<string>();

function register(dict: Record<string, Entry>) {
  if (lang === 'fr') return;
  const i = lang === 'en' ? 0 : 1;
  for (const [fr, tr] of Object.entries(dict)) {
    const out = tr[i];
    if (!out) continue;
    if (fr.includes('${')) {
      // Texte fixe exact (espaces compris, souples) entre les morceaux variables : « ${a} h ${b} » exige « h » isolé.
      const parts = fr.replace(/[’]/g, '\'').replace(/\s+/g, ' ').trim().split(/\$\{[^}]*\}/);
      const esc = (x: string) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/ /g, '\\s+');
      const re = new RegExp('^' + parts.map(esc).join('(.+?)') + '$');
      patterns.push({ re, out, n: parts.length - 1, fixed: parts.join('').replace(/\s/g, '').length });
    } else if (/<[a-z]/i.test(fr)) html.set(norm(fr), out);
    else { exact.set(norm(fr), out); outputs.add(norm(out)); }
  }
  // Les plus précis d'abord : plus de texte fixe, puis moins de morceaux variables.
  patterns.sort((a, b) => b.fixed - a.fixed || a.n - b.n);
}
for (const d of [dictApp, dictCreate, dictVoice, dictTalk, dictStage, dictWorld]) register(d);

/** Garde les espaces de bord du texte d'origine (nœuds de texte entre deux balises). */
function keepEdges(src: string, out: string) {
  const a = /^\s*/.exec(src)![0], b = /\s*$/.exec(src)![0];
  return a + out + b;
}

/** Textes rencontrés sans traduction (outil de contrôle : window.__i18nMissing). */
export const missing = new Set<string>();

/** Traduit une phrase française (inchangée en français ou si elle est inconnue). */
export function t(s: string): string {
  if (lang === 'fr' || !s) return s;
  const k = norm(s);
  if (!k) return s;
  if (outputs.has(k) && !exact.has(k)) return s;
  const v = exact.get(k);
  if (v !== undefined) return keepEdges(s, v);
  // Libellé mis en minuscules par le code (« Aléatoire : forme de la tête ») : clé capitalisée, traduction en minuscule.
  const c0 = k.charAt(0);
  if (c0 !== c0.toUpperCase()) {
    const vc = exact.get(c0.toUpperCase() + k.slice(1));
    if (vc !== undefined) return keepEdges(s, vc.charAt(0).toLowerCase() + vc.slice(1));
  }
  for (const p of patterns) {
    const m = p.re.exec(k);
    if (m) return keepEdges(s, p.out.replace(/\{(\d)\}/g, (_, i: string) => t(m[Number(i)] ?? '')));
  }
  if (missing.size < 2000 && /[A-Za-zÀ-ÿ]{3}/.test(k)) missing.add(k);
  return s;
}

// ---------------------------------------------------------------------------------------------- page
const ATTRS = ['title', 'aria-label', 'placeholder', 'alt'];
/** Dernier texte écrit par le traducteur, par nœud : on ne retraduit pas sa propre traduction. */
const done = new WeakMap<Node, string>();

function translateText(n: Text) {
  const s = n.data;
  if (done.get(n) === s || !/[A-Za-zÀ-ÿ]/.test(s)) return;
  const v = t(s);
  done.set(n, v);
  if (v !== s) n.data = v;
}

function translateAttr(el: Element, a: string) {
  const s = el.getAttribute(a);
  if (!s) return;
  const v = t(s);
  if (v !== s) el.setAttribute(a, v);
}

function translateTree(n: Node) {
  if (n.nodeType === 3) { translateText(n as Text); return; }
  if (n.nodeType !== 1) return;
  const el = n as Element;
  const tag = el.tagName;
  if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'CANVAS' || tag === 'svg' || tag === 'TEXTAREA') return;
  if (html.size && el.childElementCount && el.childElementCount < 12) {
    const v = html.get(norm(el.innerHTML));
    if (v) { el.innerHTML = v; return; }
  }
  for (const a of ATTRS) if (el.hasAttribute(a)) translateAttr(el, a);
  for (let c = el.firstChild; c; c = c.nextSibling) translateTree(c);
}

/** Traduit la page puis tout ce qui y apparaît ou change ensuite. Sans effet en français. */
export function startDomTranslation() {
  document.documentElement.lang = lang;
  (window as unknown as { __i18nMissing: Set<string> }).__i18nMissing = missing;
  if (lang === 'fr') return;
  document.title = t(document.title);
  translateTree(document.body);
  new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === 'childList') {
        m.addedNodes.forEach(translateTree);
        // Un parent dont le contenu HTML entier est une clé (texte avec balises) : retraduit en bloc.
        if (html.size && m.target.nodeType === 1 && (m.target as Element).childElementCount) {
          const el = m.target as Element, v = html.get(norm(el.innerHTML));
          if (v) el.innerHTML = v;
        }
      } else if (m.type === 'characterData') translateText(m.target as Text);
      else if (m.type === 'attributes' && m.attributeName) translateAttr(m.target as Element, m.attributeName);
    }
  }).observe(document.documentElement, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });
}

/** Sélecteur de langue (EN / FR / ES) pour la barre du haut. */
export function langSelect(): HTMLSelectElement {
  const sel = document.createElement('select');
  sel.className = 'chip lang-select';
  sel.setAttribute('aria-label', 'Language / Langue / Idioma');
  sel.title = 'Language / Langue / Idioma';
  for (const l of LANGS) {
    const o = document.createElement('option');
    o.value = l.id;
    o.textContent = l.label;
    o.title = l.name;
    sel.appendChild(o);
  }
  sel.value = lang;
  sel.addEventListener('change', () => setLang(sel.value as Lang));
  return sel;
}
